<script setup lang="ts">
  import { computed } from 'vue';

  import EyeActionIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import SmallChevronUpIcon from '@/shared/assets/icons/SmallChevronUp.svg';
  import { Checkbox, IconImage, IconRadio } from '@/shared/ui';

  import { isExternalDocument, type DocumentRevision } from '@/entities/Document';
  import type { DocumentSelectionMode } from '../../model/useDocumentSelectionModal';

  const props = withDefaults(
    defineProps<{
      revision: DocumentRevision;
      selectionMode: DocumentSelectionMode;
      formatRevisionLabel: (revision: number | undefined) => string;
      isActive: boolean;
      isSelected: boolean;
      isAlreadyAdded: boolean;
      canSelect: boolean;
      showViewAction?: boolean;
      isOlderRevision?: boolean;
      showExpandButton?: boolean;
      isExpanded?: boolean;
      rowClass?: string;
      actionIconClass?: string;
    }>(),
    {
      actionIconClass: 'h-5 w-5',
      isExpanded: false,
      isOlderRevision: false,
      rowClass: '',
      showExpandButton: false,
      showViewAction: false,
    }
  );

  const emit = defineEmits<{
    (e: 'row-click', revision: DocumentRevision): void;
    (e: 'toggle', revision: DocumentRevision): void;
    (e: 'view', revision: DocumentRevision): void;
    (e: 'expand'): void;
  }>();

  /**
   * Expanded revision: the number shows even for an external document. Without it, the child rows
   * would look identical to the document row, and the user would not know which revision they
   * ticked or opened. The document row keeps the dash for external documents, as it did before.
   */
  function getRevisionDisplay(revision: DocumentRevision): string {
    if (isExternalDocument(revision) && !props.isOlderRevision) {
      return '-';
    }

    return props.formatRevisionLabel(revision.revision);
  }

  const viewActionLabel = computed(() => {
    const documentLabel =
      props.revision.document.prefix?.trim() || props.revision.document.name || 'documento';

    if (props.revision.revision === undefined) {
      return `Visualizar ${documentLabel}`;
    }

    return `Visualizar ${documentLabel}, revisão ${props.formatRevisionLabel(props.revision.revision)}`;
  });
</script>

<template>
  <tr
    :data-testid="`revision-row-${revision.id}`"
    class="transition-colors"
    :class="[
      rowClass,
      isAlreadyAdded ? 'opacity-50' : selectionMode === 'single' ? 'cursor-pointer hover:bg-gray-01' : '',
      isActive ? 'bg-gray-02/40' : '',
    ]"
    @click="selectionMode === 'single' && emit('row-click', revision)"
  >
    <td class="py-4.5 pl-6 align-middle" @click.stop>
      <Checkbox
        v-if="selectionMode === 'multiple'"
        :id="revision.id"
        :checked="isSelected || isAlreadyAdded"
        :disabled="isAlreadyAdded || !canSelect"
        @click.stop
        @toggle="emit('toggle', revision)"
      />
      <IconRadio
        v-else
        :model-value="isActive ? String(revision.id) : ''"
        :value="String(revision.id)"
        name="document-selection"
        variant="small"
        @update:model-value="emit('row-click', revision)"
      />
    </td>
    <td class="px-4 py-4.5 text-sm text-gray-07">
      {{ revision.document.prefix?.trim() ? revision.document.prefix : '-' }}
    </td>
    <td class="px-4 py-4.5 text-sm text-gray-07">
      {{ revision.document.name ?? '-' }}
    </td>
    <td class="px-4 py-4.5 text-sm text-gray-07">
      {{ getRevisionDisplay(revision) }}
    </td>
    <td class="px-4 py-4.5">
      <div class="flex items-center gap-4">
        <button
          v-if="showViewAction"
          type="button"
          class="inline-flex shrink-0 items-center justify-center p-0 text-gray-07 transition-opacity hover:opacity-70"
          :aria-label="viewActionLabel"
          :title="viewActionLabel"
          @click.stop="emit('view', revision)"
        >
          <IconImage :src="EyeActionIcon" :alt="viewActionLabel" :class="actionIconClass" />
        </button>
        <button
          :data-testid="`toggle-revisions-${revision.documentId}`"
          type="button"
          class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-02 bg-white-01 text-gray-07 shadow-sm transition-colors hover:bg-gray-01"
          :class="{ invisible: !showExpandButton }"
          :tabindex="showExpandButton ? undefined : -1"
          :aria-expanded="isExpanded"
          aria-label="Expandir revisões anteriores"
          @click.stop="showExpandButton && emit('expand')"
        >
          <IconImage
            :src="SmallChevronUpIcon"
            alt="Expandir revisões anteriores"
            class="h-4 w-4 transition-transform duration-200"
            :class="{ 'rotate-180': !isExpanded }"
          />
        </button>
      </div>
    </td>
  </tr>
</template>
